import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft,
  CreditCard, Building2, FileText, Loader2, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import IyzicoPaymentModal from '../../components/IyzicoPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);

export default function DealerCart() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [shippingNote, setShippingNote] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [iyzicoOrder, setIyzicoOrder] = useState(null); // { orderId, amount }

  useEffect(() => { fetchCart(); }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
    } catch { toast.error('Sepet yüklenemedi'); }
    finally { setLoading(false); }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      setUpdating(true);
      if (newQuantity <= 0) {
        await axios.delete(`${API_URL}/api/cart/items/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.put(`${API_URL}/api/cart/items/${productId}?quantity=${newQuantity}`, null, { headers: { Authorization: `Bearer ${token}` } });
      }
      await fetchCart();
    } catch { toast.error('Sepet güncellenemedi'); }
    finally { setUpdating(false); }
  };

  const removeItem = (productId) => updateQuantity(productId, 0);

  const clearCart = async () => {
    try {
      setUpdating(true);
      await axios.delete(`${API_URL}/api/cart`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchCart();
      toast.success('Sepet temizlendi');
    } catch { toast.error('Sepet temizlenemedi'); }
    finally { setUpdating(false); }
  };

  const handleCheckout = async () => {
    try {
      setCheckingOut(true);
      const res = await axios.post(`${API_URL}/api/orders`, {
        payment_method: paymentMethod,
        shipping_note: shippingNote || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sipariş oluşturuldu: ${res.data.order_number}`);
      if (paymentMethod === 'credit_card') {
        // Open Iyzico modal with the new order
        setIyzicoOrder({ orderId: res.data.id, orderNumber: res.data.order_number, amount: grandTotal });
      } else {
        navigate('/dealer/orders');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Sipariş oluşturulamadı'); }
    finally { setCheckingOut(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a224f]" />
      </div>
    );
  }

  const items = cart?.items || [];
  const isEmpty = items.length === 0;
  const netTotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const kdvTotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity * (item.kdv_orani || 0) / 100), 0);
  const grandTotal = netTotal + kdvTotal;

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="dealer-cart">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/dealer/shop" className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-shop">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sepetim</h1>
          <p className="text-sm text-gray-500">{isEmpty ? 'Sepetiniz boş' : `${cart.item_count} ürün`}</p>
        </div>
      </div>

      {isEmpty ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">Sepetiniz Boş</h3>
          <p className="text-sm text-gray-500 mb-5">Ürün ekleyerek sipariş oluşturabilirsiniz.</p>
          <Link to="/dealer/shop" className="inline-flex items-center px-5 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d]">
            Ürünlere Göz At
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Cart Table */}
          <div className="lg:col-span-2 space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Sepet İçeriği</h2>
                <button onClick={clearCart} disabled={updating} className="text-xs text-[#1a224f] hover:text-[#141a3d] font-medium disabled:opacity-50" data-testid="clear-cart-btn">
                  Sepeti Temizle
                </button>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Ürün</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Birim</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Adet</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Toplam</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={item.product_id} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50 group`} data-testid={`cart-item-${item.product_id}`}>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-3">
                            {item.product_image ? (
                              <img src={item.product_image} alt={item.product_name} className="w-10 h-14 object-cover rounded shrink-0" />
                            ) : (
                              <div className="w-10 h-14 bg-gray-100 rounded flex items-center justify-center shrink-0">
                                <Package className="w-4 h-4 text-gray-300" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                              <p className="text-[11px] text-gray-400">
                                {item.product_barcode}
                                {item.iskonto_orani > 0 && <span className="ml-1 text-[#1a224f] font-medium">%{item.iskonto_orani} isk.</span>}
                                {item.kdv_orani > 0 && <span className="ml-1">+%{item.kdv_orani} KDV</span>}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {item.iskonto_orani > 0 && item.liste_fiyati > 0 ? (
                            <>
                              <p className="text-[10px] text-gray-400 line-through tabular-nums">{formatCurrency(item.liste_fiyati)}</p>
                              <p className="font-medium tabular-nums">{formatCurrency(item.unit_price)}</p>
                            </>
                          ) : (
                            <p className="font-medium tabular-nums">{formatCurrency(item.unit_price)}</p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center border border-gray-200 rounded-lg w-fit mx-auto">
                            <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={updating} className="p-1.5 hover:bg-gray-100 disabled:opacity-50" data-testid={`qty-minus-${item.product_id}`}>
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="px-3 text-sm font-semibold min-w-[32px] text-center tabular-nums">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={updating} className="p-1.5 hover:bg-gray-100 disabled:opacity-50" data-testid={`qty-plus-${item.product_id}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatCurrency(item.total_price)}</td>
                        <td className="px-2 py-2.5 text-center">
                          <button onClick={() => removeItem(item.product_id)} disabled={updating} className="p-1.5 text-gray-300 hover:text-[#3a4680] hover:bg-[#eef0f7] rounded transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-${item.product_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {items.map((item) => (
                  <div key={item.product_id} className="p-3 flex gap-3" data-testid={`cart-item-mobile-${item.product_id}`}>
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name} className="w-14 h-20 object-cover rounded shrink-0" />
                    ) : (
                      <div className="w-14 h-20 bg-gray-100 rounded flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate">{item.product_name}</p>
                      <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                        <span>{formatCurrency(item.unit_price)}</span>
                        {item.iskonto_orani > 0 && <span className="text-[#1a224f] font-medium">%{item.iskonto_orani} isk.</span>}
                      </div>
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={updating} className="p-1.5 hover:bg-gray-100 disabled:opacity-50">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="px-3 text-sm font-semibold min-w-[28px] text-center tabular-nums">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={updating} className="p-1.5 hover:bg-gray-100 disabled:opacity-50">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-gray-900 tabular-nums">{formatCurrency(item.total_price)}</p>
                          <button onClick={() => removeItem(item.product_id)} disabled={updating} className="p-1.5 text-[#3a4680] hover:bg-[#eef0f7] rounded">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden lg:sticky lg:top-20">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                <h2 className="text-sm font-semibold text-gray-900">Sipariş Özeti</h2>
              </div>
              <div className="p-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Ürün Sayısı</span>
                  <span className="font-medium tabular-nums">{cart.item_count} adet</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Net Tutar</span>
                  <span className="font-medium tabular-nums">{formatCurrency(netTotal)}</span>
                </div>
                {kdvTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">KDV</span>
                    <span className="font-medium tabular-nums">{formatCurrency(kdvTotal)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2.5 flex justify-between items-center">
                  <span className="font-medium text-gray-900">Genel Toplam</span>
                  <span className="text-lg font-bold text-[#1a224f] tabular-nums">{formatCurrency(grandTotal)}</span>
                </div>

                {!showCheckout ? (
                  <button onClick={() => setShowCheckout(true)} className="w-full py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d] transition-colors mt-3" data-testid="proceed-checkout-btn">
                    Siparişi Tamamla
                  </button>
                ) : (
                  <div className="space-y-3 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Ödeme Yöntemi</label>
                      <div className="space-y-1.5">
                        {[
                          { v: 'bank_transfer', l: 'Havale / EFT', i: Building2 },
                          { v: 'credit_card', l: 'Kredi Kartı', i: CreditCard },
                          { v: 'on_account', l: 'Cariye Yaz', i: FileText },
                        ].map(opt => (
                          <label key={opt.v} className={`flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-colors ${paymentMethod === opt.v ? 'border-[#3a4680] bg-[#eef0f7]' : 'border-gray-200 hover:border-gray-300'}`}>
                            <input type="radio" name="payment" value={opt.v} checked={paymentMethod === opt.v} onChange={(e) => setPaymentMethod(e.target.value)} className="text-[#1a224f]" />
                            <opt.i className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium">{opt.l}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Sipariş Notu (Opsiyonel)</label>
                      <textarea value={shippingNote} onChange={(e) => setShippingNote(e.target.value)} placeholder="Notlarınız..." rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a4680]/20 focus:border-[#5b67a0]" />
                    </div>
                    <button onClick={handleCheckout} disabled={checkingOut} className="w-full py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-order-btn">
                      {checkingOut ? <><Loader2 className="w-4 h-4 animate-spin" />İşleniyor...</> : 'Siparişi Onayla'}
                    </button>
                    <button onClick={() => setShowCheckout(false)} className="w-full py-1.5 text-xs text-gray-500 hover:text-gray-900">Geri Dön</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <IyzicoPaymentModal
        open={!!iyzicoOrder}
        onClose={() => { setIyzicoOrder(null); navigate('/dealer/orders'); }}
        kind="order"
        orderId={iyzicoOrder?.orderId}
        amount={iyzicoOrder?.amount}
        title="Sipariş Ödemesi"
        subtitle={iyzicoOrder ? `Sipariş #${iyzicoOrder.orderNumber}` : ''}
        onSuccess={() => navigate('/dealer/orders')}
      />
    </div>
  );
}
